/**
 * "This plan has no rates for the group's rating area" (DEV-382). Pure.
 *
 * Rate rows are narrowed to the group's rating area before pricing. When a
 * plan's rate table is area-specific but has nothing for the resolved area
 * (Twism: rates loaded as Area 12, address resolved to 13), the narrowed set
 * is empty and every premium computed from it is $0 - silently. Callers use
 * this to turn that into an explicit, named problem instead.
 */
export type RatingAreaGap = {
  /** The area the group resolved to (and that has no rates). */
  ratingAreaId: number;
  /** Areas the plan DOES have rates for, ascending. */
  availableAreaIds: number[];
};

type RateRowLike = { ratingAreaId?: number | string | null; isActive?: boolean | null };

/**
 * The gap, or null when there is none: no resolved area, an area-agnostic
 * rate table (no row carries an area), no rows at all (a different problem -
 * "no rates loaded"), or at least one row for the resolved area.
 */
export function findRatingAreaGap(
  rows: RateRowLike[] | null | undefined,
  ratingAreaId: number | string | null | undefined,
): RatingAreaGap | null {
  const target = Number(ratingAreaId);
  if (ratingAreaId == null || ratingAreaId === "" || !Number.isFinite(target)) return null;
  const active = (rows ?? []).filter((r) => r && r.isActive !== false);
  const areas = new Set<number>();
  for (const r of active) {
    if (r.ratingAreaId == null || r.ratingAreaId === "") continue;
    const n = Number(r.ratingAreaId);
    if (Number.isFinite(n)) areas.add(n);
  }
  if (areas.size === 0) return null;
  if (areas.has(target)) return null;
  return { ratingAreaId: target, availableAreaIds: Array.from(areas).sort((a, b) => a - b) };
}

/** "Gold PPO has no rates for Rating Area 13 (rates are loaded for Rating Area 12)." */
export function ratingAreaGapMessage(gap: RatingAreaGap, planName?: string | null): string {
  const who = planName && String(planName).trim() ? String(planName).trim() : "This plan";
  const have = gap.availableAreaIds.map((a) => `Rating Area ${a}`).join(", ");
  return `${who} has no rates for Rating Area ${gap.ratingAreaId}, the group's rating area (rates are loaded for ${have}). Check the group's address/rating area or the plan's rate table — it can't be priced until they match.`;
}
